import React, { useState, useMemo, useEffect, useRef, useCallback } from "react";
import { useSearchParams } from "react-router-dom";
import { useFileApi } from "../Hooks/useFileApi";
import FilePreview from "./FilePreview";
import { Alert, Box, CircularProgress } from "@mui/material";
import InfoIcon from '@mui/icons-material/Info';

export default function FilePreviewComponent() {
  const [searchParams] = useSearchParams();
  const [error, setError] = useState(null);
  const [fileUrl, setFileUrl] = useState("");
  const [isLoading, setIsLoading] = useState(true);
  const { getDownloadUrl } = useFileApi();
  
  const fetchedFilenameRef = useRef(null);
  const isFetchingRef = useRef(false);
  const getDownloadUrlRef = useRef(getDownloadUrl);
  
  useEffect(() => {
    getDownloadUrlRef.current = getDownloadUrl;
  }, [getDownloadUrl]);

  const filename = useMemo(() => {
    const search = window.location.search;
    const match = search.match(/[?&]filename=([^&]*)/);
    return match ? decodeURIComponent(match[1].replace(/\+/g, '%2B')) : null;
  }, []);
  
  const category = useMemo(() => searchParams.get("category"), [searchParams]);
  const vin = useMemo(() => searchParams.get("vin"), [searchParams]);

  const file = useMemo(
    () => ({
      filename,
      file_location: filename,
      file_type: filename && filename.includes(".") ? filename.slice(filename.lastIndexOf(".")) : "",
      category: category || "default",
      vin: vin || null,
    }),
    [filename, category, vin]
  );


  useEffect(() => {
    const fetchDownloadUrl = async () => {
      if (!filename) {
        setIsLoading(false);
        return;
      }

      if (fetchedFilenameRef.current === filename || isFetchingRef.current) {
        return;
      }

      isFetchingRef.current = true;
      setIsLoading(true);
      setError(null);
      
      try {
        const url = await getDownloadUrlRef.current(filename);
        setFileUrl(url);
        fetchedFilenameRef.current = filename;
      } catch (err) {
        console.error('Error fetching download URL:', err);
        setError(err.message || 'Failed to get file access. Please try again.');
      } finally {
        setIsLoading(false);
        isFetchingRef.current = false;
      }
    };

    fetchDownloadUrl();
  }, [filename]);

  const handleError = useCallback((message) => {
    setError(message);
  }, []);

  const infoAlert = (
    <Box sx={{ px: 3, pt: 2 }}>
      <Alert 
        severity="info" 
        icon={<InfoIcon sx={{ fontSize: 18 }} />}
        sx={{ 
          py: 0.5, 
          '& .MuiAlert-message': { py: 0.5, fontSize: '0.8rem' }
        }}
      >
        Issues with preview? Connect to GM networking/VPN or use Microsoft Edge.
      </Alert>
    </Box>
  );

  if (!filename) {
    return (
      <div>
        {infoAlert}
        <Box sx={{ p: 2 }}>
          <Alert severity="error">No file specified</Alert>
        </Box>
      </div>
    );
  }

  if (error) {
    return (
      <div>
        {infoAlert}
        <Box sx={{ p: 2 }}>
          <Alert severity="error">{error}</Alert>
        </Box>
      </div>
    );
  }

  if (isLoading) {
    return (
      <div>
        {infoAlert}
        <Box sx={{ p: 2, display: 'flex', alignItems: 'center', gap: 2 }}>
          <CircularProgress size={20} />
          <span>Loading file access...</span>
        </Box>
      </div>
    );
  }

  return (
    <div>
      {infoAlert}
      <FilePreview
        file={file}
        blobUrl={fileUrl}
        onError={handleError}
      />
    </div>
  );
}
